/*
|---------------------------------------------------
| If state persisting will be required then: https://www.npmjs.com/package/vuex-persistedstate
|---------------------------------------------------
*/
import { get, post, deletex } from 'providers/http'
import socket from 'providers/socket'

const SET_LOADING = 'setLoading'
const ADD_ALL = 'addAll'
const SET_ONE = 'setOne'
const REMOVE_ONE = 'removeOne'
const SET_FORCE_UPDATE = 'setForceUpdate'

export const REQUEST_ALL = 'requestAll'
export const REQUEST_BY_ID = 'requestById'
export const CREATE = 'create'
export const EDIT = 'edit'
export const DELETE = 'delete'

/**
 * Builds a vuex module for the given namespace/model name
 *
 * @param  {String} the namespace
 * @return {Object} the built vuex module
 *
 */
export default function (namespace) {
  let _subscribed = false

  function subscribeForChanges () {
    if (!_subscribed) {
      socket.emit('subscribeChanges', { namespace }, () => console.debug(`Subscribed for model: ${namespace}`))

      _subscribed = true
    }
  }

  const state = {
    data: {},
    requestedAll: false,  // To track if we requested the whole data
    loading: false,       // To track any kind of operation
    fetching: false,      // To track if read operation is in progress
    forceUpdate: false,
    lastUpdateTime: null,
    changeList: []
  }

  const getters = {
    loading: state => state.loading,
    data: state => (dispatch) => {
      if (!dispatch) {
        throw new Error('Please provide dispatch as paramter!')
      }

      subscribeForChanges()

      if ((!state.requestedAll || state.forceUpdate) && !state.fetching) {
        dispatch(`${namespace}/${REQUEST_ALL}`)
      }

      return Object.values(state.data).filter((item) => !item.err)
    },
    findById: state => (id, dispatch) => {
      if (!id || id === 'new') {
        console.warn('findById called without id')

        return {}
      }

      const item = state.data[id] || {}

      if (!dispatch) {
        return item
      }

      subscribeForChanges()

      if (!state.fetching && (!item.id || state.changeList.includes(id))) {
        dispatch(`${namespace}/${REQUEST_BY_ID}`, id)
      }

      return item
    }
  }

  const actions = {
    async [REQUEST_ALL] ({ commit }) {
      commit(SET_LOADING, true)
      commit('setFetching', true)

      commit('requestedAll')

      try {
        commit(ADD_ALL, await get(`api/v1/${namespace}`))
      }
      catch (err) {
        console.error(`Failed to retrieve ${namespace} from server:`, err)

        commit(ADD_ALL, [])

        throw err
      }
      finally {
        commit(SET_LOADING, false)
        commit('setFetching', false)
      }
    },
    async [REQUEST_BY_ID] ({ commit }, id) {
      commit(SET_LOADING, true)
      commit('setFetching', true)

      try {
        commit(SET_ONE, await get(`api/v1/${namespace}/${id}`))
      }
      catch (err) {
        console.error(`Failed to retrieve ${namespace}/${id} from server:`, err)

        commit(SET_ONE, { id, err })

        throw err
      }
      finally {
        commit(SET_LOADING, false)
        commit('setFetching', false)
      }
    },
    async [CREATE] ({ commit }, item) {
      commit(SET_LOADING, true)

      try {
        const created = await post(`api/v1/${namespace}/new`, item)

        commit(SET_ONE, created)
      }
      catch (err) {
        console.error(`Failed to insert ${namespace}`, err)

        throw err
      }
      finally {
        commit(SET_LOADING, false)
      }
    },
    async [EDIT] ({ commit }, item) {
      commit(SET_LOADING, true)

      try {
        await post(`api/v1/${namespace}/${item.id}`, item)

        commit(SET_ONE, item)
      }
      catch (err) {
        console.error(`Failed to edit ${namespace}/${item.id}`, err)

        throw err
      }
      finally {
        commit(SET_LOADING, false)
      }
    },
    async [DELETE] ({ commit }, item) {
      commit(SET_LOADING, true)

      try {
        await deletex(`api/v1/${namespace}/${item.id}`)

        commit(REMOVE_ONE, item)
      }
      catch (err) {
        console.error(`Failed to delete ${namespace}/${item.id}`, err)

        throw err
      }
      finally {
        commit(SET_LOADING, false)
      }
    }
  }

  let i = 0

  const mutations = {
    [ADD_ALL] (state, data = []) {
      console.time(`${namespace}-${i}-${data.length}`)

      const newStateData = {}

      for (let i = 0; i < data.length; i++) {
        const curr = data[i]

        newStateData[curr.id] = curr
      }

      state.data = newStateData

      console.timeEnd(`${namespace}-${i}-${data.length}`)

      state.forceUpdate = false
      state.toUpdateIds = []
      state.lastUpdateTime = new Date()
      i++
    },
    [SET_ONE] (state, item) {
      state.data = { ...state.data, [item.id]: item }
      state.changeList = state.changeList.filter((i) => i !== item.id)
      state.lastUpdateTime = new Date()
    },
    [REMOVE_ONE] (state, item) {
      state.data = Object.values(state.data)
        .filter(i => i.id !== item.id)
        .reduce((acc, curr) => ({ ...acc, [curr.id]: curr }), {})
      state.changeList = state.changeList.filter((i) => i !== item.id)
      state.lastUpdateTime = new Date()
    },
    [SET_LOADING] (state, loading) {
      state.loading = loading
    },
    [SET_FORCE_UPDATE] (state, forceUpdate) {
      state.forceUpdate = forceUpdate
    },
    requestedAll (state) {
      state.requestedAll = true
    },
    setFetching (state, fetching) {
      state.fetching = fetching
    },
    addToChanges (state, id) {
      state.changeList.push(id)
    }
  }

  return {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
  }
}



// WEBPACK FOOTER //
// ./src/store/moduleFactory.js